import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 1100, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const inputStyle = { width: '100%', padding: 10, marginBottom: 8, boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 };
const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 14 };
const btnPrimary = { padding: '10px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnInfo = { padding: '8px 12px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 };
const btnDanger = { padding: '6px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

export default function AdaptiveExamBuilder() {
  const navigate = useNavigate();
  
  // Exam basics
  const [examName, setExamName] = useState('');
  const [startingLevel, setStartingLevel] = useState('');
  const [startingBand, setStartingBand] = useState('');
  const [passingMark, setPassingMark] = useState(70);
  const [backupCount, setBackupCount] = useState(1);
  
  // Rule builder state
  const [availableTags, setAvailableTags] = useState([]);
  const [levelAvailability, setLevelAvailability] = useState({});
  const [questionRules, setQuestionRules] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  
  // Generation state
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  // Fetch tags when level+band change
  useEffect(() => {
    if (startingLevel && startingBand) {
      fetchTagStats();
    }
  }, [startingLevel, startingBand]);

  const fetchTagStats = async () => {
    setLoadingTags(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/tags/english/stats?level=${startingLevel}&band=${startingBand}`);
      if (!res.ok) throw new Error('Failed to fetch tag stats');
      const data = await res.json();
      setAvailableTags(data.tags);
      setLevelAvailability(data.levelAvailability);
    } catch (err) {
      setError(err.message);
      setAvailableTags([]);
    } finally {
      setLoadingTags(false);
    }
  };

  const addRule = () => {
    setQuestionRules(prev => [...prev, { level: startingLevel, tag: '', quantity: 1 }]);
  };

  const updateRule = (index, field, value) => {
    setQuestionRules(prev => prev.map((rule, i) => 
      i === index ? { ...rule, [field]: field === 'quantity' ? Math.max(1, parseInt(value) || 1) : value } : rule
    ));
  };

  const removeRule = (index) => {
    setQuestionRules(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (!examName.trim()) { setError('Exam name is required'); return; }
    if (!startingLevel || !startingBand) { setError('Starting Level and Band are required'); return; }
    
    // Validate rules
    const validRules = questionRules.filter(r => r.tag && r.quantity > 0);
    if (validRules.length === 0) {
      setError('Add at least one rule with a tag and quantity > 0');
      return;
    }

    setGenerating(true); setError(''); setSuccess(null); setPreview(null);
    
    try {
      const res = await fetch(`${API_BASE}/admin/exams/auto-adaptive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exam_name: examName.trim(),
          starting_level: startingLevel,
          starting_band: startingBand,
          passing_mark: passingMark,
          backup_count: backupCount,
          question_rules: validRules
        })
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      
      setSuccess({ examId: data.exam_id, primary: data.primary_count, backup: data.backup_count });
      setPreview(data.preview);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const totalPrimary = questionRules.reduce((sum, r) => sum + (r.quantity || 0), 0);
  const estimatedTotal = totalPrimary + (totalPrimary * backupCount);

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>🤖 Adaptive Exam Builder</h2>
        <button onClick={() => navigate('/admin/dashboard')} style={btnSecondary}>← Back</button>
      </div>

      {error && <div style={{ padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15 }}>{error}</div>}
      {success && (
        <div style={{ padding: 15, background: '#d4edda', color: '#155724', borderRadius: 6, marginBottom: 15 }}>
          ✅ Adaptive exam created! 
          <strong> {success.primary} primary + {success.backup} backup questions.</strong>
          <div style={{ marginTop: 8 }}>
            <button onClick={() => navigate(`/admin/exam-preview/${success.examId}`)} style={{ ...btnInfo, marginRight: 8 }}>Preview Exam</button>
            <button onClick={() => navigate('/admin/view-exams')} style={btnSecondary}>View All Exams</button>
          </div>
        </div>
      )}

      {/* Step 1: Exam Basics */}
      <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>1. Exam Settings</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
          <div>
            <label style={labelStyle}>Exam Name *</label>
            <input style={inputStyle} value={examName} onChange={e => setExamName(e.target.value)} placeholder="e.g. Level 2 Adaptive Grammar" />
          </div>
          <div>
            <label style={labelStyle}>Passing Mark (%)</label>
            <input type="number" style={inputStyle} value={passingMark} onChange={e => setPassingMark(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))} min="0" max="100" />
          </div>
          <div>
            <label style={labelStyle}>Starting Level *</label>
            <select style={inputStyle} value={startingLevel} onChange={e => setStartingLevel(e.target.value)}>
              <option value="">Select Level</option>
              {['1','2','3','4','5','6'].map(l => <option key={l} value={l}>Level {l}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Starting Band *</label>
            <select style={inputStyle} value={startingBand} onChange={e => setStartingBand(e.target.value)} disabled={!startingLevel}>
              <option value="">Select Band</option>
              {['A1','A2','B1','B2','C1','C2'].map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Backup Questions per Wrong Answer</label>
            <select style={inputStyle} value={backupCount} onChange={e => setBackupCount(parseInt(e.target.value))}>
              <option value="1">1 backup question</option>
              <option value="2">2 backup questions</option>
            </select>
            <small style={{ color: '#666', fontSize: 11 }}>If student answers wrong, system asks backup(s) of same level+band+tag</small>
          </div>
        </div>
      </div>

      {/* Step 2: Rule Builder */}
      {startingLevel && startingBand && (
        <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
            <h3 style={{ margin: 0 }}>2. Question Rules</h3>
            {loadingTags && <span style={{ color: '#666', fontSize: 13 }}>Loading tags...</span>}
          </div>

          {availableTags.length === 0 && !loadingTags && (
            <p style={{ color: '#666', fontStyle: 'italic' }}>No tags found for Level {startingLevel} + Band {startingBand}. Try different criteria.</p>
          )}

          {availableTags.length > 0 && (
            <>
              <div style={{ marginBottom: 15, padding: 10, background: '#e7f1ff', borderRadius: 6, fontSize: 13 }}>
                <strong>Estimated Questions:</strong> {totalPrimary} primary + {(totalPrimary * backupCount)} backup = <strong>{estimatedTotal} total</strong>
              </div>
              
              {/* Rule List */}
              <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, marginBottom: 15 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa' }}>
                    <tr>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Level</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Tag</th>
                      <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Quantity</th>
                      <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Available</th>
                      <th style={{ padding: '10px', borderBottom: '2px solid #ddd' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {questionRules.map((rule, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px' }}>
                          <select 
                            style={{ ...inputStyle, width: 80, marginBottom: 0 }}
                            value={rule.level}
                            onChange={e => updateRule(index, 'level', e.target.value)}
                          >
                            {Object.keys(levelAvailability).map(l => (
                              <option key={l} value={l}>Lvl {l} ({levelAvailability[l]})</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '10px' }}>
                          <select 
                            style={{ ...inputStyle, width: 120, marginBottom: 0 }}
                            value={rule.tag}
                            onChange={e => updateRule(index, 'tag', e.target.value)}
                          >
                            <option value="">Select Tag</option>
                            {availableTags.map(t => (
                              <option key={t.tag} value={t.tag}>{t.tag} ({t.available})</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <input 
                            type="number" 
                            min="1" 
                            max="20"
                            style={{ ...inputStyle, width: 60, marginBottom: 0, textAlign: 'center' }}
                            value={rule.quantity}
                            onChange={e => updateRule(index, 'quantity', e.target.value)}
                          />
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                          {availableTags.find(t => t.tag === rule.tag)?.available || '—'}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <button onClick={() => removeRule(index)} style={{ ...btnDanger, padding: '4px 8px', fontSize: 12 }}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <button onClick={addRule} style={{ ...btnInfo, marginBottom: 15 }}>+ Add Another Rule</button>
            </>
          )}
        </div>
      )}

      {/* Step 3: Generate */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={() => { setExamName(''); setStartingLevel(''); setStartingBand(''); setQuestionRules([]); }} style={btnSecondary}>Reset</button>
        <button 
          onClick={handleGenerate} 
          disabled={generating || !examName || !startingLevel || !startingBand || questionRules.filter(r => r.tag && r.quantity > 0).length === 0}
          style={{ ...btnPrimary, opacity: (generating || !examName || !startingLevel || !startingBand || questionRules.filter(r => r.tag && r.quantity > 0).length === 0) ? 0.6 : 1 }}
        >
          {generating ? '🔄 Generating Adaptive Exam...' : `🚀 Generate Exam (~${estimatedTotal} questions)`}
        </button>
      </div>

      {/* Preview (after generation) */}
      {preview && preview.length > 0 && (
        <div style={{ marginTop: 25, background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
          <h4 style={{ marginTop: 0 }}>✅ Preview: Primary Questions + Backups</h4>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#555' }}>
            {preview.map((q, i) => (
              <li key={i}>
                <strong>Q{i+1}:</strong> {q.text} 
                {q.backups > 0 && <span style={{ color: '#17a2b8', marginLeft: 8 }}>+{q.backups} backup(s) if wrong</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}